import { useState, useEffect, Fragment, useContext, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Use unified date utils
import { moment } from '../utils/dateUtils';

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  Paper,
  Link,
  Tooltip,
  IconButton,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Divider,
  useMediaQuery,
  useTheme,
  SwipeableDrawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Rating,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';

// Icons
import CancelIcon from '@mui/icons-material/Cancel';
import CircleIcon from '@mui/icons-material/Circle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TimerIcon from '@mui/icons-material/Timer';
import TimerOffIcon from '@mui/icons-material/TimerOff';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BuildIcon from '@mui/icons-material/Build';
import NavigationIcon from '@mui/icons-material/Navigation';
import PhoneIcon from '@mui/icons-material/Phone';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SmsIcon from '@mui/icons-material/Sms';
import SendIcon from '@mui/icons-material/Send';
import StarIcon from '@mui/icons-material/Star';

// Custom Components
import { Spinner, DateSwitcher, BrowserTitle } from '../components';
import WarrantyTicketsBanner from '../components/WarrantyTicketsBanner';

// Context
import { AuthContext } from '../context/AuthProvider';

// Utils
import messages from '../static/messages';

// Services
import appointmentsServices from '../services/appointmentsServices';
import smsServices from '../services/smsServices';
import clientsServices from '../services/clientsServices';

// Hooks
import usePagination from '../hooks/usePagination';

// Styles
import { table_container_base } from '../static/styles';

const link_styles = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '100%',
};

// Status config
const statusConfig = {
  'Active': { color: '#4caf50', bgColor: '#e8f5e9', icon: CircleIcon, label: 'Active' },
  'In Progress': { color: '#ff9800', bgColor: '#fff3e0', icon: CircleIcon, label: 'In Progress' },
  'Completed': { color: '#2196f3', bgColor: '#e3f2fd', icon: CheckCircleIcon, label: 'Completed' },
  'Cancelled': { color: '#f44336', bgColor: '#ffebee', icon: CancelIcon, label: 'Cancelled' },
};

// Time Block Card Component — info only, no links/buttons
const TimeBlockCard = ({ appointment }) => {
  const status = statusConfig[appointment.status] || statusConfig['Active'];
  const StatusIcon = status.icon;
  const [fullInfo, setFullInfo] = useState(null);

  // Load full details (including createdBy) for the time block
  useEffect(() => {
    if (appointment?.id) {
      const load = async () => {
        try {
          const response = await appointmentsServices.getAppointmentById(appointment.id);
          if (response.data) setFullInfo(response.data);
        } catch (err) {
          console.log('Error loading time block details:', err);
        }
      };
      load();
    }
  }, [appointment?.id]);

  const info = fullInfo || appointment;

  return (
    <Card
      sx={{
        mb: 2,
        borderRadius: 3,
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        border: '2px solid rgba(245,124,0,0.2)',
        overflow: 'visible',
        position: 'relative',
      }}
    >
      {/* Status Badge */}
      <Chip
        icon={<StatusIcon sx={{ fontSize: 14, color: status.color + ' !important' }} />}
        label={status.label}
        size="small"
        sx={{
          position: 'absolute',
          top: -10,
          right: 16,
          backgroundColor: status.bgColor,
          color: status.color,
          fontWeight: 600,
          fontSize: 11,
          height: 24,
          '& .MuiChip-icon': { color: status.color },
        }}
      />

      <CardContent sx={{ pt: 3, pb: 2 }}>
        {/* Header */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#f57c00', mb: 2 }}>
          Time Block
        </Typography>

        {/* Technician */}
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Technician:</strong>{' '}
          {info?.user?.fullName || `${info?.user?.firstName || ''} ${info?.user?.lastName || ''}`.trim() || '—'}
        </Typography>

        {/* Date */}
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Date:</strong>{' '}
          {info?.startTime ? moment(info.startTime).tz('America/Los_Angeles').format('MM/DD/YYYY') : '—'}
        </Typography>

        {/* Time */}
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Time:</strong>{' '}
          {info?.startTime ? moment(info.startTime).tz('America/Los_Angeles').format('h:mm A') : '?'}
          {' — '}
          {info?.endTime ? moment(info.endTime).tz('America/Los_Angeles').format('h:mm A') : '?'}
        </Typography>

        {/* Reason */}
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Reason:</strong> {info?.notes?.[0]?.note || 'Not specified'}
        </Typography>

        {/* Created By */}
        <Typography variant="body2" sx={{ mb: 0, color: '#666' }}>
          <strong>Created By:</strong>{' '}
          {info?.createdBy
            ? `${info.createdBy.fullName || ''}${info.createdAt ? ` — ${moment(info.createdAt).tz('America/Los_Angeles').format('MM/DD/YYYY h:mm A')}` : ''}`
            : '—'}
        </Typography>
      </CardContent>
    </Card>
  );
};

// Mobile Appointment Card Component
const AppointmentCard = ({ appointment, onCheckIn, onCheckOut, userRole }) => {
  const navigate = useNavigate();
  const status = statusConfig[appointment.status] || statusConfig['Active'];
  const StatusIcon = status.icon;
  const [navDialogOpen, setNavDialogOpen] = useState(false);
  const [confirmSmsOpen, setConfirmSmsOpen] = useState(false);
  const [confirmCheckAction, setConfirmCheckAction] = useState(null);
  const [smsSending, setSmsSending] = useState(false);

  // Detect Time Block — render info-only card
  const isTimeBlock = appointment.type?.id === 36 || appointment.type?.name === 'Time Block';
  if (isTimeBlock) {
    return <TimeBlockCard appointment={appointment} />;
  }

  const contactClient = appointment.contactClient || appointment.property?.clients?.[0]?.client;
  const clientPhone = contactClient?.phoneNumber || contactClient?.mobile;
  const clientName = contactClient?.firstName || '';

  const openNavigation = () => {
    const address = encodeURIComponent(appointment.property?.address || '');
    window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_blank');
  };

  const handleNavigateClick = () => {
    setNavDialogOpen(true);
  };

  const handleNavigateOnly = () => {
    setNavDialogOpen(false);
    openNavigation();
  };

  const handleNavigateAndNotify = () => {
    setNavDialogOpen(false);
    setConfirmSmsOpen(true);
  };

  const handleConfirmSms = async () => {
    setSmsSending(true);
    try {
      const response = await smsServices.sendEtaNotification({
        clientPhone,
        clientName,
        appointmentId: appointment.id,
      });
      if (response.data?.success) {
        toast.success('SMS sent to client');
      } else {
        toast.error(response.data?.message || 'Failed to send SMS');
      }
    } catch (error) {
      toast.error('Failed to send SMS');
    } finally {
      setSmsSending(false);
      setConfirmSmsOpen(false);
      openNavigation();
    }
  };
  
  const handleCall = () => {
    if (clientPhone) {
      window.location.href = `tel:${clientPhone}`;
    } else {
      toast.info('No phone number available');
    }
  };
  
  const handleCardClick = () => {
    navigate(`/appointments/${appointment.id}`);
  };

  return (
    <Card 
      sx={{ 
        mb: 2, 
        borderRadius: 3,
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        border: `2px solid ${status.color}20`,
        overflow: 'visible',
        position: 'relative',
      }}
    >
      {/* Status Badge */}
      <Chip
        icon={<StatusIcon sx={{ fontSize: 14, color: status.color + ' !important' }} />}
        label={status.label}
        size="small"
        sx={{
          position: 'absolute',
          top: -10,
          right: 16,
          backgroundColor: status.bgColor,
          color: status.color,
          fontWeight: 600,
          fontSize: 11,
          height: 24,
          '& .MuiChip-icon': { color: status.color },
        }}
      />
      
      <CardContent sx={{ pt: 3, pb: 1 }}>
        {/* Time + Check In/Out (top row) */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
          <AccessTimeIcon sx={{ color: '#1976d2', fontSize: 20, flexShrink: 0 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2', flex: 1, mr: 1 }}>
            {(appointment?.preferredStartTime || appointment?.startTime) ? moment(appointment.preferredStartTime || appointment.startTime).tz('America/Los_Angeles').format('h:mm A') : '?'} - {(appointment?.preferredEndTime || appointment?.endTime) ? moment(appointment.preferredEndTime || appointment.endTime).tz('America/Los_Angeles').format('h:mm A') : '?'}
          </Typography>
          {(userRole === 'technician' || userRole === 'superadmin') && (
            <>
              {!appointment?.checkIn ? (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<TimerIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setConfirmCheckAction('checkIn')}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 13,
                    py: 0.5,
                    px: 1.5,
                    minWidth: 'auto',
                    backgroundColor: '#4caf50',
                    '&:hover': { backgroundColor: '#43a047' },
                    whiteSpace: 'nowrap',
                  }}
                >
                  Check In
                </Button>
              ) : !appointment?.checkOut ? (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<TimerOffIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setConfirmCheckAction('checkOut')}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 13,
                    py: 0.5,
                    px: 1.5,
                    minWidth: 'auto',
                    backgroundColor: '#ff9800',
                    '&:hover': { backgroundColor: '#f57c00' },
                    whiteSpace: 'nowrap',
                  }}
                >
                  Check Out
                </Button>
              ) : (
                <Chip
                  label={`Done ${moment(appointment.checkOut).tz('America/Los_Angeles').format('h:mm A')}`}
                  size="small"
                  sx={{
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                />
              )}
            </>
          )}
        </Box>
        
        {/* Type */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <BuildIcon sx={{ color: '#666', mr: 1, fontSize: 18 }} />
          <Typography variant="body1" sx={{ fontWeight: 600, color: '#333' }}>
            {appointment.type?.name || 'Service'}
          </Typography>
        </Box>
        
        {/* Address - clickable to open details */}
        <Box 
          onClick={handleCardClick}
          sx={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            mb: 2,
            cursor: 'pointer',
            p: 1.5,
            mx: -1.5,
            borderRadius: 2,
            backgroundColor: '#f5f5f5',
            '&:active': { backgroundColor: '#eeeeee' },
          }}
        >
          <LocationOnIcon sx={{ color: '#e91e63', mr: 1, fontSize: 20, mt: 0.3 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 500, lineHeight: 1.4 }}>
              {appointment.property?.address || 'No address'}
            </Typography>
            {appointment.property?.zipCode && (
              <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                ZIP: {appointment.property.zipCode}
              </Typography>
            )}
          </Box>
          <ChevronRightIcon sx={{ color: '#999', ml: 1 }} />
        </Box>
        
        {/* Quick Actions */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<NavigationIcon />}
            onClick={handleNavigateClick}
            sx={{ 
              flex: 1, 
              borderRadius: 2,
              textTransform: 'none',
              borderColor: '#1976d2',
              color: '#1976d2',
              py: 1.2,
              fontSize: 15,
              fontWeight: 600,
            }}
          >
            Navigate
          </Button>
          <Button
            variant="outlined"
            startIcon={<PhoneIcon />}
            onClick={handleCall}
            sx={{ 
              flex: 1, 
              borderRadius: 2,
              textTransform: 'none',
              borderColor: '#4caf50',
              color: '#4caf50',
              py: 1.2,
              fontSize: 15,
              fontWeight: 600,
            }}
          >
            Call
          </Button>
        </Box>
      </CardContent>

      {/* Navigate Action Dialog */}
      <Dialog
        open={navDialogOpen}
        onClose={() => setNavDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, mx: 2, width: '100%' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Navigate to Client
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {appointment.property?.address}
          </Typography>
          <Button
            fullWidth
            variant="contained"
            startIcon={<NavigationIcon />}
            onClick={handleNavigateOnly}
            sx={{
              mb: 1.5,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 15,
              backgroundColor: '#1976d2',
            }}
          >
            Navigate Only
          </Button>
          <Button
            fullWidth
            variant="contained"
            startIcon={<SmsIcon />}
            onClick={handleNavigateAndNotify}
            disabled={!clientPhone}
            sx={{
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 15,
              backgroundColor: '#f57c00',
              '&:hover': { backgroundColor: '#ef6c00' },
            }}
          >
            Navigate & Notify Client
          </Button>
          {!clientPhone && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
              No client phone number available
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNavDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* SMS Confirmation Dialog */}
      <Dialog
        open={confirmSmsOpen}
        onClose={() => !smsSending && setConfirmSmsOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, mx: 2, width: '100%' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SmsIcon sx={{ color: '#f57c00' }} />
            Confirm SMS Notification
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            An SMS will be sent to <strong>{clientName || 'the client'}</strong> at <strong>{clientPhone}</strong> notifying them that you are on the way.
          </Typography>
          <Box sx={{ 
            p: 2, 
            borderRadius: 2, 
            backgroundColor: '#f5f5f5', 
            border: '1px solid #e0e0e0',
          }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#555' }}>
              "Airmaxx: Hi{clientName ? ` ${clientName}` : ''}! Your technician [Your Name] is on his way. If you need to reach him, please call [your phone]. We look forward to seeing you soon!"
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setConfirmSmsOpen(false)} 
            disabled={smsSending}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={smsSending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
            onClick={handleConfirmSms}
            disabled={smsSending}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: '#f57c00',
              '&:hover': { backgroundColor: '#ef6c00' },
            }}
          >
            {smsSending ? 'Sending...' : 'Send & Navigate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Check In / Check Out Confirmation Dialog */}
      <Dialog
        open={!!confirmCheckAction}
        onClose={() => setConfirmCheckAction(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {confirmCheckAction === 'checkIn' ? 'Confirm Check In' : 'Confirm Check Out'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {confirmCheckAction === 'checkIn'
              ? 'Are you sure you want to Check In? The current time will be recorded.'
              : 'Are you sure you want to Check Out? The current time will be recorded.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setConfirmCheckAction(null)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (confirmCheckAction === 'checkIn') onCheckIn(appointment.id);
              else onCheckOut(appointment);
              setConfirmCheckAction(null);
            }}
            sx={{
              backgroundColor: confirmCheckAction === 'checkIn' ? '#4caf50' : '#ff9800',
              '&:hover': { backgroundColor: confirmCheckAction === 'checkIn' ? '#43a047' : '#f57c00' },
            }}
          >
            {confirmCheckAction === 'checkIn' ? 'Check In' : 'Check Out'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

const Schedule = () => {
  const [appointments, setAppointments] = useState();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize date from URL, sessionStorage, or default to today — so that
  // "Back to Schedule" from an appointment returns to the date the user was
  // browsing, not to today (same pattern as Appointments.js).
  const getInitialDate = () => {
    const urlDate = searchParams.get('date');
    if (urlDate && moment(urlDate).isValid()) {
      return moment(urlDate).format();
    }
    const savedDate = sessionStorage.getItem('schedule_date');
    if (savedDate && moment(savedDate).isValid()) {
      return moment(savedDate).format();
    }
    return moment().format();
  };

  const [date, setDateState] = useState(getInitialDate);

  // Wrapper for setDate that also persists the choice to URL and sessionStorage
  const setDate = useCallback((newDate) => {
    setDateState(newDate);
    const dateStr = moment(newDate).format('YYYY-MM-DD');
    sessionStorage.setItem('schedule_date', dateStr);
    setSearchParams(prev => {
      prev.set('date', dateStr);
      return prev;
    }, { replace: true });
  }, [setSearchParams]);
  const [accessRestricted, setAccessRestricted] = useState(false);

  // Rating dialog state (shown on Check Out)
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [ratingAppointment, setRatingAppointment] = useState(null);
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingClientId, setRatingClientId] = useState('');
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  // Get userData from context
  const { userData } = useContext(AuthContext);
  const isTechnician = userData?.role === 'technician';
  
  // Check if user has extended permissions (All Proposals or All Installations)
  const hasExtendedPermissions = userData?.permissions?.includes('all_proposals') || 
                                  userData?.permissions?.includes('all_installations');

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination();

  // Async functions
  const getUserAppointments = useCallback(async (id) => {
    if (!id) return;
    try {
      const response = await appointmentsServices.getAllAppointments(date.split('T')[0] + 'T08:00:00.000Z', id);

      if (response.status === 206) {
        // Hide cancelled appointments — they are not part of a personal
        // daily schedule (no work to do) and only clutter the timeline,
        // stats and the desktop table. Cancelled ones live on the
        // dedicated /cancelled-appointments page.
        const sortedAppointments = [...response.data.data]
          .filter((a) => a.status !== 'Cancelled')
          .sort((a, b) => moment(a.startTime).valueOf() - moment(b.startTime).valueOf());
        setAppointments(sortedAppointments);
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  }, [date]);

  const addTimeStamp = async (id, mode) => {
    const currentDate = moment().tz('America/Los_Angeles');

    try {
      const response = await appointmentsServices.update(id, {
        ...(mode === 'checkIn' && { checkIn: moment.utc(currentDate).format() }),
        ...(mode === 'checkOut' && { checkOut: moment.utc(currentDate).format() }),
      });

      if (response.status === 200) {
        toast.success(mode === 'checkIn' ? '✓ Checked In!' : '✓ Checked Out!');
        getUserAppointments(userData?.id);
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const handleCheckOutClick = (appointment) => {
    const isTimeBlock = appointment.type?.id === 36 || appointment.type?.name === 'Time Block';
    const clients = appointment?.property?.clients || [];

    if (isTimeBlock || clients.length === 0) {
      addTimeStamp(appointment.id, 'checkOut');
      return;
    }

    const defaultClient = clients.find(cp => cp.client?.id === appointment?.contactClient?.id)?.client
      || clients[0]?.client;
    setRatingAppointment(appointment);
    setRatingClientId(defaultClient?.id || '');
    setRatingValue(defaultClient?.rating || 0);
    setRatingDialogOpen(true);
  };

  const handleRatingSubmitAndCheckOut = async () => {
    if (ratingValue === 0 || !ratingAppointment) return;

    const clients = ratingAppointment?.property?.clients || [];
    const selectedCp = clients.find(cp => cp.client?.id === ratingClientId);
    const client = selectedCp?.client;

    setRatingSubmitting(true);
    try {
      if (client?.id) {
        await clientsServices.update(client.id, { rating: ratingValue });
      }

      const currentDate = moment().tz('America/Los_Angeles');
      const response = await appointmentsServices.update(ratingAppointment.id, {
        checkOut: moment.utc(currentDate).format(),
      });

      if (response.status === 200) {
        toast.success('✓ Checked Out!');
        getUserAppointments(userData?.id);
        setRatingDialogOpen(false);
        setRatingAppointment(null);
        setRatingValue(0);
        setRatingClientId('');
      }
    } catch (error) {
      console.error('Failed to check out:', error);
      toast.error('Failed to complete check out. Please try again.');
    } finally {
      setRatingSubmitting(false);
    }
  };

  // Check if technician has access (after 7:30 AM for current day only)
  // Technicians with All Proposals or All Installations permissions are exempt from this restriction
  useEffect(() => {
    if (isTechnician && !hasExtendedPermissions) {
      const now = moment().tz('America/Los_Angeles');
      const today = moment().tz('America/Los_Angeles').startOf('day');
      const selectedDay = moment(date).tz('America/Los_Angeles').startOf('day');
      const accessStartTime = today.clone().set({ hour: 7, minute: 30, second: 0 });
      
      // If selected date is today and current time is before 7:30 AM
      if (selectedDay.isSame(today, 'day') && now.isBefore(accessStartTime)) {
        setAccessRestricted(true);
        setAppointments([]);
        return;
      }
      
      // If selected date is in the future (shouldn't happen with maxDate, but just in case)
      if (selectedDay.isAfter(today, 'day')) {
        setAccessRestricted(true);
        setAppointments([]);
        return;
      }
      
      setAccessRestricted(false);
    } else if (hasExtendedPermissions) {
      // Technicians with extended permissions have no restrictions
      setAccessRestricted(false);
    }
  }, [date, isTechnician, hasExtendedPermissions]);

  // Hooks
  useEffect(() => {
    if (userData?.id && !accessRestricted) {
      getUserAppointments(userData.id);
    }
  }, [date, userData?.id, getUserAppointments, accessRestricted]);

  if (!appointments) return <Spinner />;

  // Mobile View
  if (isMobile) {
    return (
      <Box sx={{ px: 2, pb: 4 }}>
        <BrowserTitle title="My Schedule | Airmaxx Pro" />
        
        {/* Header */}
        <Box sx={{ 
          position: 'sticky', 
          top: 0, 
          zIndex: 10, 
          backgroundColor: '#fff',
          pt: 1,
          pb: 2,
          mx: -2,
          px: 2,
        }}>
          <WarrantyTicketsBanner />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: '#1a237e' }}>
            My Schedule
          </Typography>
          {/* DateSwitcher with maxDate restriction for technicians (except those with extended permissions) */}
          <DateSwitcher 
            date={date} 
            setDate={setDate} 
            maxDate={(isTechnician && !hasExtendedPermissions) ? moment().tz('America/Los_Angeles').format() : null}
          />
        </Box>
        
        {/* Access Restricted Message for Technicians (not for those with extended permissions) */}
        {accessRestricted && isTechnician && !hasExtendedPermissions && (
          <Box sx={{ 
            textAlign: 'center', 
            py: 8,
            px: 2,
          }}>
            <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
              🕢 Schedule Not Available Yet
            </Typography>
            <Typography variant="body2" sx={{ color: '#999' }}>
              Your schedule will be available at 7:30 AM
            </Typography>
          </Box>
        )}

        {/* Stats Bar - only show if not restricted */}
        {!accessRestricted && (
          <>
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              mb: 2,
              overflowX: 'auto',
              pb: 1,
            }}>
              <Chip 
                label={`${appointments.length} appointments`} 
                sx={{ backgroundColor: '#e3f2fd', fontWeight: 600 }} 
              />
              <Chip 
                label={`${appointments.filter(a => a.status === 'Completed').length} completed`} 
                sx={{ backgroundColor: '#e8f5e9', fontWeight: 600 }} 
              />
              <Chip 
                label={`${appointments.filter(a => !a.checkIn).length} pending`} 
                sx={{ backgroundColor: '#fff3e0', fontWeight: 600 }} 
              />
            </Box>
            
            {/* Appointments List */}
            {appointments.length > 0 ? (
              appointments.map((appointment) => (
                <AppointmentCard
                  key={appointment.id}
                  appointment={appointment}
                  onCheckIn={(id) => addTimeStamp(id, 'checkIn')}
                  onCheckOut={(apt) => handleCheckOutClick(apt)}
                  userRole={userData?.role}
                />
              ))
            ) : (
              <Box sx={{ 
                textAlign: 'center', 
                py: 8,
                px: 2,
              }}>
                <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
                  No appointments for today
                </Typography>
                <Typography variant="body2" sx={{ color: '#999' }}>
                  Check back later for updates
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* Client Rating Dialog (Check Out) */}
        <Dialog
          open={ratingDialogOpen}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown
        >
          <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <StarIcon sx={{ color: '#ff9800' }} />
            Rate Client Before Check Out
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
              Please rate the client before completing the appointment.
            </Typography>

            {(ratingAppointment?.property?.clients || []).length > 1 && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="rating-client-label">Rate client</InputLabel>
                <Select
                  labelId="rating-client-label"
                  value={ratingClientId}
                  label="Rate client"
                  onChange={(e) => {
                    const newId = e.target.value;
                    setRatingClientId(newId);
                    const cp = (ratingAppointment?.property?.clients || []).find(c => c.client?.id === newId);
                    setRatingValue(cp?.client?.rating || 0);
                  }}
                >
                  {(ratingAppointment?.property?.clients || []).map((cp) => (
                    <MenuItem key={cp.client?.id} value={cp.client?.id}>
                      {cp.client?.fullName || `${cp.client?.firstName || ''} ${cp.client?.lastName || ''}`.trim()}
                      {cp.client?.id === ratingAppointment?.contactClient?.id ? ' (preferred)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <Rating
                value={ratingValue}
                onChange={(_, newValue) => setRatingValue(newValue || 0)}
                size="large"
                sx={{ fontSize: '3rem' }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, flexDirection: 'column', gap: 1 }}>
            <Button
              variant="contained"
              onClick={handleRatingSubmitAndCheckOut}
              disabled={ratingValue === 0 || ratingSubmitting}
              fullWidth
              startIcon={ratingSubmitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
              sx={{
                backgroundColor: '#ff9800',
                '&:hover': { backgroundColor: '#f57c00' },
                '&.Mui-disabled': { backgroundColor: '#e0e0e0' },
              }}
            >
              {ratingSubmitting ? 'Processing...' : ratingValue > 0 ? 'Check Out' : 'Rate to Continue'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Desktop View
  return (
    <Box>
      <BrowserTitle title="Schedule | Airmaxx Pro" />

      <WarrantyTicketsBanner />

      {/* DateSwitcher with maxDate restriction for technicians (except those with extended permissions) */}
      <DateSwitcher 
        date={date} 
        setDate={setDate} 
        maxDate={(isTechnician && !hasExtendedPermissions) ? moment().tz('America/Los_Angeles').format() : null}
      />

      {/* Access Restricted Message for Technicians */}
      {accessRestricted && isTechnician && !hasExtendedPermissions && (
        <Box sx={{ 
          textAlign: 'center', 
          py: 8,
          px: 2,
        }}>
          <Typography variant="h5" sx={{ color: '#666', mb: 1 }}>
            🕢 Schedule Not Available Yet
          </Typography>
          <Typography variant="body1" sx={{ color: '#999' }}>
            Your schedule will be available at 7:30 AM
          </Typography>
        </Box>
      )}

      {!accessRestricted && (
      <>
      <TableContainer component={Paper} sx={{ ...table_container_base, mt: 2 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, width: { md: '5%' } }}>Status</TableCell>
              <TableCell sx={{ width: { md: '50%' } }}>Address</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, width: '10%' }}>Type</TableCell>
              <TableCell sx={{ width: { md: '15%' } }}>Time Frame</TableCell>
              {(userData?.role === 'technician' || userData?.role === 'superadmin') && (
                <TableCell sx={{ width: '5%' }}>Actions</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {appointments.length ? (
              appointments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((appointment) => (
                <TableRow key={appointment.id} hover>
                  <TableCell scope="row" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Link
                      href={`/appointments/${appointment.id}`}
                      sx={{ ...link_styles, justifyContent: 'center' }}
                      underline="none"
                      color="rgba(0, 0, 0, 0.87)"
                    >
                      {appointment.status === 'Active' && (
                        <CircleIcon sx={{ color: 'green', mr: 1, width: 15, height: 15 }} />
                      )}
                      {appointment.status === 'In Progress' && (
                        <CircleIcon sx={{ color: 'orange', mr: 1, width: 15, height: 15 }} />
                      )}
                      {appointment.status === 'Completed' && (
                        <CheckCircleIcon sx={{ color: 'green', mr: 1, width: 15, height: 15 }} />
                      )}
                      {appointment.status === 'Cancelled' && (
                        <CancelIcon sx={{ color: 'red', mr: 1, width: 15, height: 15 }} />
                      )}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link
                      href={`/appointments/${appointment.id}`}
                      color="rgba(0, 0, 0, 0.87)"
                      underline="none"
                      sx={link_styles}
                    >
                      {appointment.property?.address}
                    </Link>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Link
                      href={`/appointments/${appointment.id}`}
                      color="rgba(0, 0, 0, 0.87)"
                      underline="none"
                      sx={link_styles}
                    >
                      {appointment.type?.name || '-'}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link
                      href={`/appointments/${appointment.id}`}
                      color="rgba(0, 0, 0, 0.87)"
                      underline="none"
                      sx={link_styles}
                    >
                      {(appointment?.preferredStartTime || appointment?.startTime) ? moment(appointment.preferredStartTime || appointment.startTime).tz('America/Los_Angeles').format('hh:mm A') : '?'} -{' '}
                      {(appointment?.preferredEndTime || appointment?.endTime) ? moment(appointment.preferredEndTime || appointment.endTime).tz('America/Los_Angeles').format('hh:mm A') : '?'}
                    </Link>
                  </TableCell>
                  {(userData?.role === 'technician' || userData?.role === 'superadmin') && (
                    <TableCell sx={{ textAlign: 'center' }}>
                      {!appointment?.checkIn && (
                        <Tooltip title="Check In">
                          <IconButton onClick={() => addTimeStamp(appointment.id, 'checkIn')}>
                            <TimerIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      {appointment?.checkIn && !appointment?.checkOut && (
                        <Tooltip title="Check Out">
                          <IconButton onClick={() => handleCheckOutClick(appointment)}>
                            <TimerOffIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: 'center' }}>
                  <strong>No Data</strong>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={appointments?.length || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Rows per page"
      />
      </>
      )}

      {/* Client Rating Dialog (Check Out) */}
      <Dialog
        open={ratingDialogOpen}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <StarIcon sx={{ color: '#ff9800' }} />
          Rate Client Before Check Out
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
            Please rate the client before completing the appointment.
          </Typography>

          {(ratingAppointment?.property?.clients || []).length > 1 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="rating-client-desktop-label">Rate client</InputLabel>
              <Select
                labelId="rating-client-desktop-label"
                value={ratingClientId}
                label="Rate client"
                onChange={(e) => {
                  const newId = e.target.value;
                  setRatingClientId(newId);
                  const cp = (ratingAppointment?.property?.clients || []).find(c => c.client?.id === newId);
                  setRatingValue(cp?.client?.rating || 0);
                }}
              >
                {(ratingAppointment?.property?.clients || []).map((cp) => (
                  <MenuItem key={cp.client?.id} value={cp.client?.id}>
                    {cp.client?.fullName || `${cp.client?.firstName || ''} ${cp.client?.lastName || ''}`.trim()}
                    {cp.client?.id === ratingAppointment?.contactClient?.id ? ' (preferred contact)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <Rating
              value={ratingValue}
              onChange={(_, newValue) => setRatingValue(newValue || 0)}
              size="large"
              sx={{ fontSize: '3rem' }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="contained"
            onClick={handleRatingSubmitAndCheckOut}
            disabled={ratingValue === 0 || ratingSubmitting}
            startIcon={ratingSubmitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
            sx={{
              backgroundColor: '#ff9800',
              '&:hover': { backgroundColor: '#f57c00' },
              '&.Mui-disabled': { backgroundColor: '#e0e0e0' },
            }}
          >
            {ratingSubmitting ? 'Processing...' : ratingValue > 0 ? 'Check Out' : 'Rate to Continue'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Schedule;
